import {EditorState, type Extension, RangeSetBuilder, StateField} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view'
import {clsx} from 'clsx'
import type {Element, Nodes} from 'hast'
import {visitParents} from 'unist-util-visit-parents'

import {getDelimiterType} from '../../language-tools/html/extensions/delimiters'
import {hastPropertiesToHtmlAttributes, isTag} from '../../language-tools/html/hast-utils'
import {isChecked} from '../../language-tools/markdown/list-utils'
import {isPositionedNode} from '../../language-tools/syntax-tree/positioned-node'
import {CharacterReferenceWidget} from '../widgets/character-reference'
import {EmptySpanWidget} from '../widgets/empty-span'
import {VideoElementWidget} from '../widgets/video-element'
import {VoidElementWidget} from '../widgets/void-element'
import {activeNodesChanged, activeNodesExtension, getActiveNodes} from './active-nodes'
import {getHtmlTree, htmlTreeChanged, markdownParserExtension} from './markdown-parser'
import styles from './style-contents.module.css'

/** Ancestor node types with data about whether they are active or not. For handling children of active nodes. */
type NodeContext = Array<{node: Nodes; active: boolean}>

const withinActiveTable = (context: NodeContext) => context.some(parent => parent.active && isTag(parent.node, 'table'))

// `root` is always active when the editor is focused since it covers the entire document
const withinActiveBlock = (context: NodeContext) =>
  context.some(parent => parent.active && parent.node.type === 'element')

/** Check if this element is a link defined with `[]()` syntax. */
const isResourceLink = (element: Element) =>
  element.tagName === 'a' &&
  element.children.some(child => child.type === 'element' && child.properties?.dataDelimiter === 'resource')

// These aren't exported so we have to extract them from the function parameters :(
type DecorationSpec = Parameters<typeof Decoration.mark>[0] | Parameters<typeof Decoration.replace>[0]

/** Tag names to style as blocks. */
const blockElements = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'dl',
  'p',
  'table',
  'hr',
  'div',
  'details',
  'video',
])

/** Check if this node is a setext heading node with only one character in the delimiter line. */
function isSingleCharSetextHeading(element: Element) {
  return element.children.some(
    child =>
      child.type === 'element' &&
      child.tagName === 'span' &&
      child.properties?.dataDelimiter === 'setextHeadingLine' &&
      child.children?.[0]?.type === 'text' &&
      child.children[0].value.trim().length === 1,
  )
}

function getElementMark(element: Element, active: boolean, context: NodeContext): DecorationSpec | undefined {
  const baseClassName = clsx(active && styles.activeBlock, blockElements.has(element.tagName) && styles.block)

  switch (element.tagName) {
    // In an active block, links render with only the `resourceDestination` delimiter node highlighted
    case 'a':
      if (withinActiveBlock(context) && isResourceLink(element)) return undefined
      break

    // Tables revert to plain text when active (for now, until we build a table editor)
    case 'table':
      if (active) return {tagName: 'div', class: baseClassName}
      break
    case 'tr':
    case 'th':
    case 'td':
    case 'thead':
    case 'tbody':
      if (withinActiveTable(context)) return undefined
      break

    // Details/summary tags are not supported in contenteditable
    case 'details':
      return {tagName: 'div', class: baseClassName}
    case 'summary':
      return {tagName: 'div', class: styles.summary}

    // Images and void elements must be rendered with widgets since they fully replace their contents rather than wrapping them
    case 'img':
    case 'hr':
      return active ? {tagName: 'div', class: baseClassName} : {widget: new VoidElementWidget(element, baseClassName)}
    case 'video':
      return active ? {tagName: 'div', class: baseClassName} : {widget: new VideoElementWidget(element, baseClassName)}

    // In contenteditable we always rely on soft breaks for line breaks, so we can just ignore `<br>` elements entirely
    case 'br':
      return undefined

    // Delimiter special cases
    case 'span': {
      const delimiterType = getDelimiterType(element)
      if (!delimiterType) break
      const raw = element.children[0]?.type === 'text' ? element.children[0].value : ''

      // character reference delimiters have to be replaced with the reference value via a widget
      if (delimiterType === 'characterReference' && !withinActiveBlock(context))
        return {widget: new CharacterReferenceWidget(raw)}

      // Links are wierd because we move the anchor to the destination url when active
      if (delimiterType === 'resourceDestination' && withinActiveBlock(context))
        return {tagName: 'a', attributes: {href: raw}}

      // Tasklist checkboxes are rendered as disabled checkboxes when not in an active block
      if (delimiterType === 'taskListCheck' && !withinActiveBlock(context)) {
        const checked = isChecked(raw)
        const checkboxElement: Element = {
          type: 'element',
          tagName: 'input',
          properties: {
            type: 'checkbox',
            disabled: true,
            'aria-label': checked ? 'Checked task list item' : 'Unchecked task list item',
            ...(checked ? {checked: true} : {}),
          },
          children: [],
        }
        return {widget: new VoidElementWidget(checkboxElement)}
      }

      break
    }

    case 'h2':
      // Avoid flicker when typing lists that immediately follow text (https://github.com/github/core-ux/issues/838)
      if (active && isSingleCharSetextHeading(element)) return {tagName: 'div', class: baseClassName}
      break
  }

  const {className: nodeClassName, ...properties} = element.properties
  return {
    tagName: element.tagName,
    class: clsx(baseClassName, nodeClassName),
    attributes: hastPropertiesToHtmlAttributes(properties),
  }
}

function getNodeMark(node: Nodes, active: boolean, context: NodeContext): DecorationSpec | undefined {
  switch (node.type) {
    case 'comment':
      return {
        tagName: 'code',
        class: styles.comment,
      }
    case 'element':
      return getElementMark(node, active, context)
  }
}

function buildDecorations(state: EditorState): DecorationSet {
  const tree = getHtmlTree(state)
  const activeNodes = getActiveNodes(state)

  const builder = new RangeSetBuilder<Decoration>()

  visitParents(tree, (node, parents) => {
    if (!isPositionedNode(node)) return

    const nodeContext: NodeContext = parents.map(p => ({node: p, active: activeNodes.has(p)}))

    const markSpec = getNodeMark(node, activeNodes.has(node), nodeContext)
    if (markSpec) {
      // Use `inclusive: true` so the mark includes its start and end positions.
      // This prevents rendering issues when a replace decoration is applied at a node's start or end
      // and would otherwise conflict with the mark.
      const mark = 'widget' in markSpec ? Decoration.replace(markSpec) : Decoration.mark({...markSpec, inclusive: true})

      // For decorations with the same 'from' and 'to' value, CodeMirror renders the first tag in the set and wraps
      // the second tag around it. However, our tree walker encounters parents first and then children, which causes
      // incorrect element order. For example, a list with one element would result in `<li><ol>...</ol></li>`
      // because the list and item have the same start and end offsets.
      //
      // To fix this, CodeMirror supports a secondary comparison by `startSide`. Lower `startSide` = outermost
      // element.
      mark.startSide = parents.length
      builder.add(node.position.start.offset, node.position.end.offset, mark)
    }
  })

  // Place an empty `span` element at the very end of the document, following any line breaks. The DOM will always
  // suppress a line break at the end of a block container, which would prevent the user from typing a newline at the
  // end of the doc. By placing an invisible span after the last character, we ensure that a trailing line break is
  // never the last thing in the document.
  if (isPositionedNode(tree))
    builder.add(
      tree.position.end.offset,
      tree.position.end.offset,
      Decoration.widget({
        widget: EmptySpanWidget,
        // `side` determines where the cursor ends up relative to this element. Positive ensures the cursor appears before
        // the element, ensuring that it remains 'attached' to text. This also determines order if two decorations have
        // the same position, so we use the max value to ensure this always renders at the very end of the doc.
        side: 10_000,
      }),
    )

  return builder.finish()
}

const decorationsState = StateField.define<DecorationSet>({
  create: buildDecorations,
  update: (prev, tr) => {
    const treeChanged = htmlTreeChanged(tr)

    if (tr.docChanged && !treeChanged) {
      // 'mapping' the decorations just updates their positions based on where text was inserted/deleted to keep them
      // relatively in sync position-wise. It prevents text from flowing out of the end of nodes, but doesn't
      // actually reflect any Markdown formatting changes. For that, we wait until the tree parse event which we know
      // will quickly follow a docChanged event.
      return prev.map(tr.changes)
    } else if (treeChanged || activeNodesChanged(tr)) {
      return buildDecorations(tr.state)
    } else {
      return prev
    }
  },
  provide: field => [markdownParserExtension, activeNodesExtension, EditorView.outerDecorations.from(field)],
})

export const styleContentsExtension: Extension = decorationsState
